import moment from "moment";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";

import {
  MoneyFlowGameCriteriaDto,
  MoneyFlowPlayerCriteriaDto,
  MoneyFlowRequestDto,
  MoneyFlowRequestDtoPeriodEnum,
  NullableRangeDtoInteger,
  RangeDtoLong,
  TechnologyPresenceDto,
} from "@/shared/api";

/**
 * Interface for the error state object
 * Each field corresponds to a mutation function and can be null (no error) or a string (error message)
 */
export interface MoneyFlowRequestErrorState {
  // Main property errors
  period: null | string;

  // Game criteria errors
  gameCriteriaRoomId: null | string;
  gameCriteriaGameType: null | string;
  gameCriteriaTimestampPeriod: null | string;
  gameCriteriaGameSize: null | string;
  gameCriteriaNonHeroCount: null | string;
  gameCriteriaHeroCount: null | string;
  gameCriteriaOpponentCount: null | string;
  gameCriteriaStraddle: null | string;
  gameCriteriaAntes: null | string;
  gameCriteriaBigBlinds: null | string;
  gameCriteriaBigBlindUsdRange: null | string;
  gameCriteriaTablesIds: null | string;
  gameCriteriaTechnologiesPresence: null | string;
  gameCriteriaNoTechnologyPresenceRange: null | string;

  gameCriteriaTechnologyPresence: Array<{
    technology_name: null | string;
    presence_range: null | string;
  }>;

  // Hero criteria errors
  heroCriteriaTechnologies: null | string;
  heroCriteriaGroups: null | string;
  heroCriteriaAlliances: null | string;
  heroCriteriaClubs: null | string;
  heroCriteriaPlayersIds: null | string;
  heroCriteriaAiInstances: null | string;
  heroCriteriaAiProfiles: null | string;
  heroCriteriaAiIds: null | string;
  heroCriteriaClusteringPatterns: null | string;

  // Opponent criteria errors
  opponentCriteriaTechnologies: null | string;
  opponentCriteriaGroups: null | string;
  opponentCriteriaAlliances: null | string;
  opponentCriteriaClubs: null | string;
  opponentCriteriaPlayersIds: null | string;
  opponentCriteriaAiInstances: null | string;
  opponentCriteriaAiProfiles: null | string;
  opponentCriteriaAiIds: null | string;
  opponentCriteriaClusteringPatterns: null | string;

  // Sub opponents criteria errors
  subOpponentsCriteria: Array<{
    global: null | string;
    technologies: null | string;
    groups: null | string;
    alliances: null | string;
    clubs: null | string;
    playersIds: null | string;
    aiInstances: null | string;
    aiProfiles: null | string;
    aiIds: null | string;
    clusteringPatterns: null | string;
  }>;
}

// Default values for creating new objects
// export const createDefaultRangeDtoLong = (): RangeDtoLong => ({
//   gte: 0,
//   lte: 0,
// });
//
// export const createDefaultRangeDtoInteger = (): NullableRangeDtoInteger => ({
//   gte: undefined,
//   lte: undefined,
// });

export const createDefaultTechnologyPresenceDto = (): TechnologyPresenceDto => ({
  technology_name: "adv",
  presence_range: {
    gte: 1,
    lte: 12,
  },
});

export const createDefaultMoneyFlowPlayerCriteriaDto = (): MoneyFlowPlayerCriteriaDto => ({
  technologies: [],
  groups: [],
  clubs: [],
  players_ids: [],
  ai_instances: [],
  ai_profiles: [],
  ai_ids: [],
  clustering_patterns: [],
});

export const createDefaultMoneyFlowGameCriteriaDto = (): MoneyFlowGameCriteriaDto => ({
  room_id: "",
  game_type: "",
  alliances: [],
  clubs: [],
  timestamp_period: {
    gte: moment().startOf("month").valueOf(),
    lte: moment().valueOf(),
  },
  game_size: {
    gte: undefined,
    lte: undefined,
  },
  non_hero_count: {
    gte: undefined,
    lte: undefined,
  },
  hero_count: {
    gte: undefined,
    lte: undefined,
  },
  opponent_count: {
    gte: undefined,
    lte: undefined,
  },
  straddle: false,
  antes: [],
  big_blinds: [],
  big_blind_usd_range: {
    gte: undefined,
    lte: undefined,
  },
  tables_ids: [],
  technologies_presence: [],
  no_technology_presence_range: {
    gte: 2,
    lte: 12,
  },
});

export const createDefaultMoneyFlowRequestDto = (): MoneyFlowRequestDto => ({
  period: MoneyFlowRequestDtoPeriodEnum.Day,
  game_criteria: createDefaultMoneyFlowGameCriteriaDto(),
  hero_criteria: createDefaultMoneyFlowPlayerCriteriaDto(),
  opponent_criteria: createDefaultMoneyFlowPlayerCriteriaDto(),
  sub_opponents_criteria: [],
});

/**
 * Creates a default error state object with all fields set to null
 * @returns Default error state object
 */
export const createDefaultErrorState = (): MoneyFlowRequestErrorState => ({
  // Main property errors
  period: null,

  // Game criteria errors
  gameCriteriaRoomId: null,
  gameCriteriaGameType: null,
  gameCriteriaTimestampPeriod: null,
  gameCriteriaGameSize: null,
  gameCriteriaNonHeroCount: null,
  gameCriteriaHeroCount: null,
  gameCriteriaOpponentCount: null,
  gameCriteriaStraddle: null,
  gameCriteriaAntes: null,
  gameCriteriaBigBlinds: null,
  gameCriteriaBigBlindUsdRange: null,
  gameCriteriaTablesIds: null,
  gameCriteriaTechnologiesPresence: null,
  gameCriteriaNoTechnologyPresenceRange: null,
  gameCriteriaTechnologyPresence: [],

  // Hero criteria errors
  heroCriteriaTechnologies: null,
  heroCriteriaGroups: null,
  heroCriteriaAlliances: null,
  heroCriteriaClubs: null,
  heroCriteriaPlayersIds: null,
  heroCriteriaAiInstances: null,
  heroCriteriaAiProfiles: null,
  heroCriteriaAiIds: null,
  heroCriteriaClusteringPatterns: null,

  // Opponent criteria errors
  opponentCriteriaTechnologies: null,
  opponentCriteriaGroups: null,
  opponentCriteriaAlliances: null,
  opponentCriteriaClubs: null,
  opponentCriteriaPlayersIds: null,
  opponentCriteriaAiInstances: null,
  opponentCriteriaAiProfiles: null,
  opponentCriteriaAiIds: null,
  opponentCriteriaClusteringPatterns: null,

  // Sub opponents criteria errors
  subOpponentsCriteria: [],
});

/**
 * Hook for managing MoneyFlowRequestDto state and providing mutation functions
 * @param initialState - Optional initial state for the MoneyFlowRequestDto
 * @returns Object containing the current state and functions to mutate it
 */
export const useStatisticVisualizationFilters = (initialState?: MoneyFlowRequestDto) => {
  const [state, setState] = useState<MoneyFlowRequestDto>(
    initialState || createDefaultMoneyFlowRequestDto()
  );

  const [anteOptions, setAnteOptions] = useState(() => {
    return state.game_criteria.antes;
  });
  const [bigBlindOptions, setBigBlindOptions] = useState(() => {
    return state.game_criteria.big_blinds;
  });
  const [tableIdOptions, setTableIdOptions] = useState(() => {
    return state.game_criteria.tables_ids;
  });
  const [groupIdOptions, setGroupIdOptions] = useState(() => {
    return state.hero_criteria.groups.filter((item) => !!item);
  });
  const [technologyOptions, setTechnologyOptions] = useState(() => {
    return state.hero_criteria.technologies.filter((item) => !!item);
  });
  const [alliancesOptions, setAlliancesOptions] = useState(() => {
    return state.game_criteria.alliances.filter((item) => !!item);
  });
  const [clubOptions, setClubOptions] = useState(() => {
    return state.hero_criteria.clubs.filter((item) => !!item);
  });
  const [playerIdsOptions, setPlayerIOptions] = useState(() => {
    return state.hero_criteria.players_ids.filter((item) => !!item);
  });
  const [aiInstancesOptions, setAiInstancesOptions] = useState(() => {
    return state.hero_criteria.ai_instances.filter((item) => !!item);
  });
  const [aiProfilesOptions, setAiProfilesOptions] = useState(() => {
    return state.hero_criteria.ai_profiles.filter((item) => !!item);
  });
  const [aiIdsOptions, setAiIdsOptions] = useState(() => {
    return state.hero_criteria.ai_ids.filter((item) => !!item);
  });
  const [clusteringPatternOptions, setClusteringPatternOptions] = useState(() => {
    return state.hero_criteria.clustering_patterns.filter((item) => !!item);
  });

  const [errors, setErrors] = useState<MoneyFlowRequestErrorState>(createDefaultErrorState());

  // Reset state to default or provided initial state
  const resetState = useCallback(() => {
    setState(initialState || createDefaultMoneyFlowRequestDto());
  }, [initialState]);

  // Reset errors to default (all null)
  const resetErrors = useCallback(() => {
    setErrors(createDefaultErrorState());
  }, []);

  // Set error for a specific field
  const setError = useCallback(
    (field: keyof MoneyFlowRequestErrorState, error: string | null) => {
      setErrors((prev) => ({
        ...prev,
        [field]: error,
      }));
    },
    []
  );

  // Clear error for a specific field
  const clearError = useCallback((field: keyof MoneyFlowRequestErrorState) => {
    setErrors((prev) => ({
      ...prev,
      [field]: null,
    }));
  }, []);

  // Clear all errors
  const clearAllErrors = useCallback(() => {
    resetErrors();
  }, [resetErrors]);

  // Update period
  const setPeriod = useCallback((period: MoneyFlowRequestDtoPeriodEnum) => {
    setState((prev) => ({ ...prev, period }));
  }, []);

  // Update specific field in game criteria
  const updateGameCriteria = useCallback(
    <K extends keyof MoneyFlowGameCriteriaDto>(
      field: K,
      value: MoneyFlowGameCriteriaDto[K]
    ) => {
      setState((prev) => ({
        ...prev,
        game_criteria: {
          ...prev.game_criteria,
          [field]: value,
        },
      }));
    },
    []
  );

  // Individual update methods for each field in game criteria
  const updateGameCriteriaRoomId = useCallback(
    (value: string) => {
      clearError("gameCriteriaRoomId");
      updateGameCriteria("room_id", value);
    },
    [updateGameCriteria, clearError]
  );

  const updateGameCriteriaGameType = useCallback(
    (value: string) => {
      updateGameCriteria("game_type", value);
    },
    [updateGameCriteria]
  );

  const updateGameCriteriaTimestampPeriod = useCallback(
    (value: RangeDtoLong) => {
      updateGameCriteria("timestamp_period", value);
    },
    [updateGameCriteria]
  );

  const updateGameCriteriaStraddle = useCallback(
    (value: boolean | undefined) => {
      updateGameCriteria("straddle", value);
    },
    [updateGameCriteria]
  );

  const updateGameCriteriaAnte = useCallback(
    (value: boolean | undefined) => {
      updateGameCriteria("ante", value);
    },
    [updateGameCriteria]
  );

  const updateGameCriteriaAntes = useCallback(
    (value: Array<number>) => {
      updateGameCriteria("antes", value);
    },
    [updateGameCriteria]
  );

  const updateGameCriteriaBigBlinds = useCallback(
    (value: Array<number>) => {
      updateGameCriteria("big_blinds", value);
    },
    [updateGameCriteria]
  );

  const updateGameCriteriaBigBlindUsdRange = useCallback(
    (value: NullableRangeDtoInteger) => {
      updateGameCriteria("big_blind_usd_range", value);
    },
    [updateGameCriteria]
  );

  const updateGameCriteriaTablesIds = useCallback(
    (value: Array<string>) => {
      updateGameCriteria("tables_ids", value);
    },
    [updateGameCriteria]
  );

  const updateGameCriteriaClubs = useCallback(
    (value: Array<string>) => {
      updateGameCriteria("clubs", value);
    },
    [updateGameCriteria]
  );

  const updateGameCriteriaTechnologiesPresence = useCallback(
    (value: Array<TechnologyPresenceDto>) => {
      updateGameCriteria("technologies_presence", value);
    },
    [updateGameCriteria]
  );

  const updateGameCriteriaNoTechnologyPresenceRange = useCallback(
    (value: NullableRangeDtoInteger) => {
      updateGameCriteria("no_technology_presence_range", value);
    },
    [updateGameCriteria]
  );

  // Add a new technology presence to game criteria
  const addGameCriteriaTechnologyPresence = useCallback(
    (technologyPresence?: TechnologyPresenceDto) => {
      setState((prev) => ({
        ...prev,
        game_criteria: {
          ...prev.game_criteria,
          technologies_presence: [
            ...prev.game_criteria.technologies_presence,
            technologyPresence || createDefaultTechnologyPresenceDto(),
          ],
        },
      }));
    },
    []
  );

  // Update a technology presence field by index
  const updateGameCriteriaTechnologyPresenceField = useCallback(
    <K extends keyof TechnologyPresenceDto>(
      index: number,
      field: K,
      value: TechnologyPresenceDto[K]
    ) => {
      setState((prev) => {
        const newTechnologiesPresence = [...prev.game_criteria.technologies_presence];
        if (newTechnologiesPresence[index]) {
          newTechnologiesPresence[index] = {
            ...newTechnologiesPresence[index],
            [field]: value,
          };
        }
        return {
          ...prev,
          game_criteria: {
            ...prev.game_criteria,
            technologies_presence: newTechnologiesPresence,
          },
        };
      });
    },
    []
  );

  // Individual update methods for each field in technology presence by index
  const updateGameCriteriaTechnologyPresenceName = useCallback(
    (index: number, value: string) => {
      updateGameCriteriaTechnologyPresenceField(index, "technology_name", value);
    },
    [updateGameCriteriaTechnologyPresenceField]
  );

  const updateGameCriteriaTechnologyPresenceRange = useCallback(
    (index: number, value: NullableRangeDtoInteger) => {
      updateGameCriteriaTechnologyPresenceField(index, "presence_range", value);
    },
    [updateGameCriteriaTechnologyPresenceField]
  );

  // Update specific field in hero criteria
  const updateHeroCriteria = useCallback(
    <K extends keyof MoneyFlowPlayerCriteriaDto>(
      field: K,
      value: MoneyFlowPlayerCriteriaDto[K]
    ) => {
      setState((prev) => ({
        ...prev,
        hero_criteria: {
          ...prev.hero_criteria,
          [field]: value,
        },
      }));
    },
    []
  );

  // Individual update methods for each field in hero criteria
  const updateHeroCriteriaTechnologies = useCallback(
    (value: Array<string>) => {
      updateHeroCriteria("technologies", value);
    },
    [updateHeroCriteria]
  );

  const updateHeroCriteriaGroups = useCallback(
    (value: Array<string>) => {
      updateHeroCriteria("groups", value);
    },
    [updateHeroCriteria]
  );

  const updateHeroCriteriaClubs = useCallback(
    (value: Array<string>) => {
      updateHeroCriteria("clubs", value);
    },
    [updateHeroCriteria]
  );

  const updateHeroCriteriaPlayersIds = useCallback(
    (value: Array<string>) => {
      updateHeroCriteria("players_ids", value);
    },
    [updateHeroCriteria]
  );

  const updateHeroCriteriaAiInstances = useCallback(
    (value: Array<string>) => {
      updateHeroCriteria("ai_instances", value);
    },
    [updateHeroCriteria]
  );

  const updateHeroCriteriaAiProfiles = useCallback(
    (value: Array<string>) => {
      updateHeroCriteria("ai_profiles", value);
    },
    [updateHeroCriteria]
  );

  const updateHeroCriteriaAiIds = useCallback(
    (value: Array<string>) => {
      updateHeroCriteria("ai_ids", value);
    },
    [updateHeroCriteria]
  );

  const updateHeroCriteriaClusteringPatterns = useCallback(
    (value: Array<string>) => {
      updateHeroCriteria("clustering_patterns", value);
    },
    [updateHeroCriteria]
  );

  // Update specific field in opponent criteria
  const updateOpponentCriteria = useCallback(
    <K extends keyof MoneyFlowPlayerCriteriaDto>(
      field: K,
      value: MoneyFlowPlayerCriteriaDto[K]
    ) => {
      setState((prev) => ({
        ...prev,
        opponent_criteria: {
          ...prev.opponent_criteria,
          [field]: value,
        },
      }));
    },
    []
  );

  // Individual update methods for each field in opponent criteria
  const updateOpponentCriteriaTechnologies = useCallback(
    (value: Array<string>) => {
      updateOpponentCriteria("technologies", value);
    },
    [updateOpponentCriteria]
  );

  const updateOpponentCriteriaGroups = useCallback(
    (value: Array<string>) => {
      updateOpponentCriteria("groups", value);
    },
    [updateOpponentCriteria]
  );

  const updateOpponentCriteriaClubs = useCallback(
    (value: Array<string>) => {
      updateOpponentCriteria("clubs", value);
    },
    [updateOpponentCriteria]
  );

  const updateOpponentCriteriaPlayersIds = useCallback(
    (value: Array<string>) => {
      updateOpponentCriteria("players_ids", value);
    },
    [updateOpponentCriteria]
  );

  const updateOpponentCriteriaAiInstances = useCallback(
    (value: Array<string>) => {
      updateOpponentCriteria("ai_instances", value);
    },
    [updateOpponentCriteria]
  );

  const updateOpponentCriteriaAiProfiles = useCallback(
    (value: Array<string>) => {
      updateOpponentCriteria("ai_profiles", value);
    },
    [updateOpponentCriteria]
  );

  const updateOpponentCriteriaAiIds = useCallback(
    (value: Array<string>) => {
      updateOpponentCriteria("ai_ids", value);
    },
    [updateOpponentCriteria]
  );

  const updateOpponentCriteriaClusteringPatterns = useCallback(
    (value: Array<string>) => {
      updateOpponentCriteria("clustering_patterns", value);
    },
    [updateOpponentCriteria]
  );

  // Add a sub opponent criteria
  const addSubOpponentCriteria = useCallback((criteria?: MoneyFlowPlayerCriteriaDto) => {
    setState((prev) => ({
      ...prev,
      sub_opponents_criteria: [
        ...prev.sub_opponents_criteria,
        criteria || createDefaultMoneyFlowPlayerCriteriaDto(),
      ],
    }));
  }, []);

  // Remove a sub opponent criteria by index
  const removeSubOpponentCriteria = useCallback((index: number) => {
    setState((prev) => ({
      ...prev,
      sub_opponents_criteria: prev.sub_opponents_criteria.filter((_, i) => i !== index),
    }));
  }, []);

  // Update specific field in a sub opponent criteria by index
  const updateSubOpponentCriteriaField = useCallback(
    <K extends keyof MoneyFlowPlayerCriteriaDto>(
      index: number,
      field: K,
      value: MoneyFlowPlayerCriteriaDto[K]
    ) => {
      setState((prev) => {
        const newSubOpponents = [...prev.sub_opponents_criteria];
        if (newSubOpponents[index]) {
          newSubOpponents[index] = { ...newSubOpponents[index], [field]: value };
        }
        return { ...prev, sub_opponents_criteria: newSubOpponents };
      });
    },
    []
  );

  // Individual update methods for each field in sub opponent criteria by index
  const updateSubOpponentCriteriaTechnologies = useCallback(
    (index: number) => {
      return (value: Array<string>) => {
        updateSubOpponentCriteriaField(index, "technologies", value);
      };
    },
    [updateSubOpponentCriteriaField]
  );

  const updateSubOpponentCriteriaGroups = useCallback(
    (index: number) => {
      return (value: Array<string>) => {
        updateSubOpponentCriteriaField(index, "groups", value);
      };
    },
    [updateSubOpponentCriteriaField]
  );

  const updateSubOpponentCriteriaClubs = useCallback(
    (index: number) => {
      return (value: Array<string>) => {
        updateSubOpponentCriteriaField(index, "clubs", value);
      };
    },
    [updateSubOpponentCriteriaField]
  );

  const updateSubOpponentCriteriaPlayersIds = useCallback(
    (index: number) => {
      return (value: Array<string>) => {
        updateSubOpponentCriteriaField(index, "players_ids", value);
      };
    },
    [updateSubOpponentCriteriaField]
  );

  const updateSubOpponentCriteriaAiInstances = useCallback(
    (index: number) => {
      return (value: Array<string>) => {
        updateSubOpponentCriteriaField(index, "ai_instances", value);
      };
    },
    [updateSubOpponentCriteriaField]
  );

  const updateSubOpponentCriteriaAiProfiles = useCallback(
    (index: number) => {
      return (value: Array<string>) => {
        updateSubOpponentCriteriaField(index, "ai_profiles", value);
      };
    },
    [updateSubOpponentCriteriaField]
  );

  const updateSubOpponentCriteriaAiIds = useCallback(
    (index: number) => {
      return (value: Array<string>) => {
        updateSubOpponentCriteriaField(index, "ai_ids", value);
      };
    },
    [updateSubOpponentCriteriaField]
  );

  const updateSubOpponentCriteriaClusteringPatterns = useCallback(
    (index: number) => {
      return (value: Array<string>) => {
        updateSubOpponentCriteriaField(index, "clustering_patterns", value);
      };
    },
    [updateSubOpponentCriteriaField]
  );

  const updateHeroCriteriaAlliances = useCallback(
    (value: Array<string>) => {
      updateGameCriteria("alliances", value);
    },
    [updateGameCriteria]
  );

  return {
    // State
    state,
    errors,

    // Reset functions
    resetState,
    resetErrors,

    // Error handling functions
    setError,
    clearError,
    clearAllErrors,

    // Main property updaters
    setPeriod,

    // Game criteria updaters
    updateGameCriteriaRoomId,
    updateGameCriteriaGameType,
    updateGameCriteriaTimestampPeriod,
    updateGameCriteriaStraddle,
    updateGameCriteriaAnte,
    updateGameCriteriaAntes,
    updateGameCriteriaBigBlinds,
    updateGameCriteriaBigBlindUsdRange,
    updateGameCriteriaTablesIds,
    updateGameCriteriaClubs,
    updateGameCriteriaTechnologiesPresence,
    updateGameCriteriaNoTechnologyPresenceRange,
    addGameCriteriaTechnologyPresence,
    updateGameCriteriaTechnologyPresenceName,
    updateGameCriteriaTechnologyPresenceRange,
    updateHeroCriteriaAlliances,
    // Hero criteria updaters
    updateHeroCriteriaTechnologies,
    updateHeroCriteriaGroups,
    updateHeroCriteriaClubs,
    updateHeroCriteriaPlayersIds,
    updateHeroCriteriaAiInstances,
    updateHeroCriteriaAiProfiles,
    updateHeroCriteriaAiIds,
    updateHeroCriteriaClusteringPatterns,

    // Opponent criteria updaters
    updateOpponentCriteriaTechnologies,
    updateOpponentCriteriaGroups,
    updateOpponentCriteriaClubs,
    updateOpponentCriteriaPlayersIds,
    updateOpponentCriteriaAiInstances,
    updateOpponentCriteriaAiProfiles,
    updateOpponentCriteriaAiIds,
    updateOpponentCriteriaClusteringPatterns,

    // Sub opponents criteria updaters
    addSubOpponentCriteria,
    removeSubOpponentCriteria,
    updateSubOpponentCriteriaTechnologies,
    updateSubOpponentCriteriaGroups,
    updateSubOpponentCriteriaClubs,
    updateSubOpponentCriteriaPlayersIds,
    updateSubOpponentCriteriaAiInstances,
    updateSubOpponentCriteriaAiProfiles,
    updateSubOpponentCriteriaAiIds,
    updateSubOpponentCriteriaClusteringPatterns,

    anteOptions,
    setAnteOptions,
    bigBlindOptions,
    setBigBlindOptions,
    tableIdOptions,
    setTableIdOptions,
    groupIdOptions,
    setGroupIdOptions,
    setTechnologyOptions,
    alliancesOptions,
    setAlliancesOptions,
    clubOptions,
    setClubOptions,
    playerIdsOptions,
    setPlayerIOptions,
    aiInstancesOptions,
    setAiInstancesOptions,
    aiProfilesOptions,
    setAiProfilesOptions,
    aiIdsOptions,
    setAiIdsOptions,
    clusteringPatternOptions,
    setClusteringPatternOptions,
    setState,
  };
};
